import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AutoAwesomeOutlined,
  BusinessOutlined,
  DeleteOutline,
  MoreVert,
  OpenInNew,
} from '@mui/icons-material';
import { formatDate } from '../utils/dateUtils';

/** Visually hidden but readable by screen readers. */
const visuallyHidden = {
  position: 'absolute',
  // Strings: in sx a bare 1 means 100%, not 1px.
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

/**
 * Compact source badge for Fathom vs Recall-backed sessions.
 * @param {object} props - Component props.
 * @param {string} props.source - Session source key.
 * @returns {JSX.Element} Badge.
 */
function SourceBadge({ source }) {
  const recall = source === 'recall';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        height: 24,
        px: 1,
        borderRadius: '6px',
        bgcolor: 'var(--q-subtle)',
        color: 'var(--q-text-2)',
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: 999, bgcolor: recall ? 'var(--q-accent)' : 'var(--q-warning)' }} />
      {recall ? 'Quill bot' : 'Fathom'}
    </Box>
  );
}

/**
 * One-line session summary preview, or a placeholder when none exists yet.
 * @param {object} props - Component props.
 * @param {string} [props.summary] - Session summary text.
 * @returns {JSX.Element} Summary text.
 */
function SessionSummary({ summary }) {
  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: 12.5,
        color: summary ? 'text.secondary' : 'var(--q-faint)',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {summary || 'No summary yet'}
    </Typography>
  );
}

/**
 * Suggested customer plus external domain hints for a session.
 * @param {object} props - Component props.
 * @param {object|null} props.suggested - Domain-based customer guess.
 * @param {Array<string>} props.domains - External domains from source metadata.
 * @returns {JSX.Element} Hint chips.
 */
function MatchHints({ suggested, domains }) {
  if (suggested) {
    return (
      <Tooltip title={domains.length ? `Attendees from ${domains.join(', ')}` : 'Matched by attendee domain'}>
        <Chip icon={<BusinessOutlined />} label={suggested.name} size="small" color="primary" sx={{ maxWidth: '100%' }} />
      </Tooltip>
    );
  }
  if (domains.length === 0) {
    return <Typography variant="caption" sx={{ color: 'var(--q-faint)' }}>No match yet</Typography>;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {domains.slice(0, 2).map((d) => (
        <Chip key={d} label={d} size="small" variant="outlined" />
      ))}
      {domains.length > 2 && (
        <Typography variant="caption" color="text.secondary">+{domains.length - 2}</Typography>
      )}
    </Box>
  );
}

/**
 * Triage actions (link / open / more) shared by the table and card layouts.
 * @param {object} props - Component props.
 * @param {object} props.session - Session the actions apply to.
 * @param {boolean} [props.stretch] - Grow the Link button (card layout).
 * @param {function(object): void} props.onLink - Open link dialog for a session.
 * @param {function(object): void} props.onDeleteRequest - Open delete confirmation.
 * @param {function(object): void} props.onViewSession - Navigate to session detail.
 * @returns {JSX.Element} Action buttons.
 */
function SessionActions({ session, stretch, onLink, onDeleteRequest, onViewSession }) {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, width: stretch ? '100%' : 'auto' }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => onLink(session)}
        sx={{ borderRadius: 999, px: 1.75, flexGrow: stretch ? 1 : 0 }}
        aria-label={`Link ${session.name} to a project`}
      >
        Link
      </Button>
      <Tooltip title="Open session">
        <IconButton size="small" aria-label={`Open ${session.name}`} onClick={() => onViewSession(session)}>
          <OpenInNew fontSize="small" />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        aria-label={`More actions for ${session.name}`}
        aria-haspopup="menu"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onDeleteRequest(session);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteOutline fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText>Delete session</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

/**
 * Dashboard triage: unlinked meeting sessions as a compact table (md and up)
 * or a stacked card list (xs/sm) that keeps the Link button on screen.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.sessions - Sessions with `project_id` null.
 * @param {function(object): object|null} props.getSuggestedCustomer - Domain-based customer guess.
 * @param {function(object): void} props.onLink - Open link dialog for a session.
 * @param {function(object): void} props.onDeleteRequest - Open delete confirmation.
 * @param {function(object): void} props.onViewSession - Navigate to session detail.
 * @param {function(): void} [props.onSuggestMatches] - Open Inky with a request to match each session to a project.
 * @returns {JSX.Element|null} Section or null when empty.
 */
export default function HomeUnlinkedSessions({
  sessions,
  getSuggestedCustomer,
  onLink,
  onDeleteRequest,
  onViewSession,
  onSuggestMatches,
}) {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

  if (!sessions?.length) {
    return null;
  }

  return (
    <Box
      component="section"
      aria-labelledby="home-unlinked-heading"
      sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 'var(--q-radius-card)', overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography id="home-unlinked-heading" component="h2" sx={{ fontSize: 15, fontWeight: 600 }}>
              Unlinked sessions
            </Typography>
            <Chip size="small" color="warning" label={sessions.length} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Link each recording to a project so its requirements land in the right workspace.
          </Typography>
        </Box>
        {onSuggestMatches && (
          <Button
            startIcon={<AutoAwesomeOutlined />}
            onClick={onSuggestMatches}
            sx={{ borderRadius: 999, bgcolor: 'var(--q-accent-soft)', color: 'var(--q-accent-strong)', '&:hover': { bgcolor: 'var(--q-accent-selected)' } }}
          >
            Ask Inky to match
          </Button>
        )}
      </Box>
      {isNarrow ? (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, borderTop: '1px solid var(--q-border-subtle)' }}>
          {sessions.map((session) => (
            <Box
              component="li"
              key={session.id}
              sx={{ px: 2, py: 1.5, '&:not(:last-of-type)': { borderBottom: '1px solid var(--q-row-divider)' } }}
            >
              <Typography sx={{ fontWeight: 500, mb: 0.25, overflowWrap: 'anywhere' }}>{session.name}</Typography>
              <SessionSummary summary={session.summary} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, my: 1 }}>
                <SourceBadge source={session.source} />
                <Typography variant="body2" color="text.secondary">{formatDate(session.created_at)}</Typography>
                <MatchHints suggested={getSuggestedCustomer(session)} domains={session.source_metadata?.external_domains || []} />
              </Box>
              <SessionActions
                session={session}
                stretch
                onLink={onLink}
                onDeleteRequest={onDeleteRequest}
                onViewSession={onViewSession}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 760 }} aria-labelledby="home-unlinked-heading">
            <TableHead>
              <TableRow sx={{ bgcolor: 'var(--q-bg-subtle)', '& th': { borderTop: '1px solid var(--q-border-subtle)', borderBottomColor: 'var(--q-border-subtle)' } }}>
                <TableCell sx={{ pl: 2.5 }}>Session</TableCell>
                <TableCell sx={{ width: 120 }}>Source</TableCell>
                <TableCell sx={{ width: 120 }}>Recorded</TableCell>
                <TableCell sx={{ width: 200 }}>Suggested customer</TableCell>
                <TableCell align="right" sx={{ pr: 2.5, width: 170 }}><Box component="span" sx={visuallyHidden}>Actions</Box></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((session) => (
                <TableRow key={session.id} hover sx={{ '& td': { borderBottomColor: 'var(--q-row-divider)' } }}>
                  <TableCell sx={{ pl: 2.5, py: 1.5 }}>
                    <Typography sx={{ fontWeight: 500, fontSize: 13.5, overflowWrap: 'anywhere' }}>{session.name}</Typography>
                    <SessionSummary summary={session.summary} />
                  </TableCell>
                  <TableCell><SourceBadge source={session.source} /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', color: 'var(--q-text-2)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(session.created_at)}
                  </TableCell>
                  <TableCell>
                    <MatchHints suggested={getSuggestedCustomer(session)} domains={session.source_metadata?.external_domains || []} />
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 2 }}>
                    <SessionActions
                      session={session}
                      onLink={onLink}
                      onDeleteRequest={onDeleteRequest}
                      onViewSession={onViewSession}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
